import React, { useEffect, useRef, useState } from "react";
import { BRANDING } from "../config/branding";
import { UI_MSG } from "../config/uiMessages";
import { MobilePageFooter } from "../trust";
import "./homePage.css";
import HomeStory from "./HomeStory";

const PLACEHOLDERS = ["Enter your post code", "Click on map", "Enter your post code"];

function TargetIcon() {
  return (
    <svg className="hm-btn-icon" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="3.2" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="12" r="7.2" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M12 1.8v3.2M12 19v3.2M1.8 12h3.2M19 12h3.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function locationMessage(code) {
  if (code === 1) return UI_MSG.LOCATION_DENIED;
  if (code === 2) return UI_MSG.LOCATION_UNAVAILABLE;
  return UI_MSG.LOCATION_TIMEOUT;
}

const LOCATE_ATTEMPTS = [
  { enableHighAccuracy: false, maximumAge: 300000, timeout: 20000 },
  { enableHighAccuracy: false, maximumAge: 0, timeout: 20000 },
  { enableHighAccuracy: true, maximumAge: 0, timeout: 25000 },
];

export default function HomePage({
  postcode,
  error,
  onClearError,
  onSubmitPostcode,
  onUseLocation,
  onOpenPage,
  onNavigateTrust,
}) {
  const [draft, setDraft] = useState("");
  const [focused, setFocused] = useState(false);
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState("");
  const mountedRef = useRef(true);
  const settledRef = useRef(false);
  const stopLocateRef = useRef(() => {});
  const pageRef = useRef(null);

  function clearErrors() {
    setLocalError("");
    onClearError?.();
  }

  useEffect(() => () => {
    mountedRef.current = false;
    stopLocateRef.current();
  }, []);

  useEffect(() => {
    const page = pageRef.current;
    if (page) page.scrollTop = 0;
    if (typeof window !== "undefined") {
      window.scrollTo(0, 0);
    }
  }, []);

  useEffect(() => {
    if (!(localError || error)) return undefined;
    const onPointer = (event) => {
      const target = event.target;
      if (target?.closest?.("#hm-postcode") || target?.closest?.(".hm-go")) return;
      clearErrors();
    };
    document.addEventListener("pointerdown", onPointer);
    return () => document.removeEventListener("pointerdown", onPointer);
  }, [localError, error]);

  async function onSearch(event) {
    event.preventDefault();
    clearErrors();
    setBusy(true);
    const ok = await onSubmitPostcode?.(draft);
    if (!mountedRef.current) return;
    setBusy(false);
    if (ok) onOpenPage?.("map");
  }

  function onLocate() {
    clearErrors();
    if (!navigator.geolocation) {
      setLocalError(UI_MSG.LOCATION_UNSUPPORTED);
      return;
    }
    if (typeof window !== "undefined" && window.isSecureContext === false) {
      setLocalError(UI_MSG.LOCATION_INSECURE);
      return;
    }

    stopLocateRef.current();
    settledRef.current = false;
    setBusy(true);

    const watchIds = [];
    const timers = [];
    let attemptIndex = 0;
    let lastCode = 3;
    let watchStarted = false;

    const stop = () => {
      watchIds.splice(0).forEach((id) => navigator.geolocation.clearWatch(id));
      timers.splice(0).forEach((id) => window.clearTimeout(id));
    };
    stopLocateRef.current = stop;

    const finish = (position) => {
      const lat = position?.coords?.latitude;
      const lon = position?.coords?.longitude;
      if (settledRef.current || !Number.isFinite(lat) || !Number.isFinite(lon)) return;
      settledRef.current = true;
      stop();
      if (!mountedRef.current) return;
      onUseLocation?.(lat, lon);
      setBusy(false);
      onOpenPage?.("map");
    };

    const fail = (err) => {
      if (settledRef.current) return;
      settledRef.current = true;
      stop();
      if (!mountedRef.current) return;
      setBusy(false);
      setLocalError(locationMessage(err?.code ?? lastCode));
    };

    const startWatchFallback = () => {
      if (settledRef.current || watchStarted) return;
      watchStarted = true;
      const id = navigator.geolocation.watchPosition(
        finish,
        (err) => {
          if (settledRef.current) return;
          if (err?.code === 1) {
            fail(err);
            return;
          }
          if (err?.code) lastCode = err.code;
        },
        { enableHighAccuracy: false, maximumAge: 60000, timeout: 30000 }
      );
      watchIds.push(id);
      timers.push(window.setTimeout(() => fail({ code: lastCode }), 32000));
    };

    const runAttempt = () => {
      if (settledRef.current) return;
      if (attemptIndex >= LOCATE_ATTEMPTS.length) {
        startWatchFallback();
        return;
      }
      const options = LOCATE_ATTEMPTS[attemptIndex];
      attemptIndex += 1;
      navigator.geolocation.getCurrentPosition(
        finish,
        (err) => {
          if (settledRef.current) return;
          if (err?.code === 1) {
            fail(err);
            return;
          }
          if (err?.code) lastCode = err.code;
          timers.push(window.setTimeout(runAttempt, 400));
        },
        options
      );
    };

    runAttempt();
  }

  const showHint = !draft && !focused;

  return (
    <div className="hm-page" ref={pageRef}>
      <section className="hm-hero">
        <div className="hm-brand" aria-label={BRANDING.appName}>
          <img className="hm-brand-logo" src={BRANDING.logoSrc} alt="" />
          <div className="hm-brand-text">
            <div className="hm-brand-name">{BRANDING.appName}</div>
            {BRANDING.tagline ? (
              <div className="hm-brand-tagline">{BRANDING.tagline}</div>
            ) : null}
          </div>
        </div>
        <h1 className="hm-title">Clear catchments. Clearer choices for your child.</h1>
        <p className="hm-lead">
          We meticulously map each grammar school&apos;s published catchment — then bring
          rankings, offers, exams, and journeys together so parents can decide with confidence.
        </p>
        <form className="hm-search" onSubmit={onSearch}>
          <label className="hm-label" htmlFor="hm-postcode">Postcode</label>
          <div className="hm-search-shell">
            <div className="hm-input-wrap">
              <span className="hm-input-icon" aria-hidden="true">
                <img src="/icons/home_Board_opaque.png" alt="" width="18" height="18" />
              </span>
              <input
                id="hm-postcode"
                className="hm-input"
                value={draft}
                placeholder=""
                autoComplete="postal-code"
                enterKeyHint="search"
                aria-invalid={Boolean(localError || error)}
                aria-describedby={localError || error ? "hm-postcode-error" : undefined}
                onChange={(event) => {
                  clearErrors();
                  setDraft(event.target.value);
                }}
                onFocus={() => {
                  clearErrors();
                  setFocused(true);
                }}
                onBlur={() => setFocused(false)}
              />
              {showHint ? (
                <span className="hm-ph" aria-hidden="true">
                  <span className="hm-ph-track">
                    {PLACEHOLDERS.map((line, index) => (
                      <span key={`${line}-${index}`}>{line}</span>
                    ))}
                  </span>
                </span>
              ) : null}
            </div>
            <button type="submit" className="hm-go" disabled={busy}>
              See schools
            </button>
          </div>
          {localError || error ? (
            <p id="hm-postcode-error" className="hm-error" role="alert">
              {localError || error}
            </p>
          ) : null}
          <div className="hm-actions">
            <button type="button" className="hm-locate" onClick={onLocate} disabled={busy}>
              <TargetIcon />
              Use current location
            </button>
            <button type="button" className="hm-mapgo" onClick={() => onOpenPage?.("map")}>
              Select on the map
            </button>
          </div>
        </form>
      </section>

      <HomeStory onOpenPage={onOpenPage} />
      <MobilePageFooter onNavigate={onNavigateTrust} />
    </div>
  );
}
